/**
 * @author Radek Liška <ing.radek.liska@gmail.com>
 * @copyright Ki-Wi Digital s.r.o.
 * @license
 * All Rights Reserved.
 * Unauthorized copying of this file is strictly prohibited.
 * Proprietary and confidential.
 */

import EventEmitter from 'eventemitter3';
import get from 'lodash/get';
import isNil from 'lodash/isNil';
import isObject from 'lodash/isObject';

import Numeric from '../numeric/numeric';
import ScheduleInfo from './schedule-info';

/**
 * Strategie přehrávání pro libovolné médium.
 * Vhodné pro použití jako samostatné médium i v rámci playlistu.
 * @implements {ScheduleStrategy}
 */
class ScheduleDeterministic {
  /**
   * Vytvoří instanci.
   * @param {Session} session Sezení s nastavením zařízení.
   * @param {Playlist} playlist Plalist, který má být deterministický.
   * @param {ViewerRegistry} viewerRegistry Registr informací o typech médií.
   * @returns {ScheduleMedium} Nová instance.
   */
  constructor(session, playlist, viewerRegistry) {
    if (!isObject(session)) {
      throw new TypeError(`Invalid session ${session}.`);
    }
    if (!isObject(playlist)) {
      throw new TypeError(`Invalid playlist ${playlist}.`);
    }
    if (!isObject(viewerRegistry)) {
      throw new TypeError(`Invalid viewer registry ${viewerRegistry}.`);
    }

    /**
     * Správce událostí.
     * @private
     * @type {EventEmitter}
     */
    this.emitter = new EventEmitter();
    this.playlist = playlist;

    this.schedulePlan = null;
    this.session = session;
    this.viewerRegistry = viewerRegistry;
  }

  /** @inheritdoc */
  destroy() {
    // Není potřeba nic rušit.
    // Přítomno pouze pro splnění rozhraní.
  }

  /**
   * Určí informace pro přehrání média.
   * @param {number} cycleIndex Pořadí cyklu, ke kterému vytvořit informace.
   * @private
   * @returns {ScheduleInfo} Informace pro přehrání média.
   */
  getInfo(cycleIndex) {
    return new ScheduleInfo({
      cycleIndex,
      iterationIndex: 0,
      playlist: this.playlist,
      session: this.session,
      viewerRegistry: this.viewerRegistry
    });
  }

  /** @inheritdoc */
  go() {
    const fromSchedulePlan = this.schedulePlan;
    const fromCycleIndex = get(fromSchedulePlan, 'cycleIndex', null);
    const toCycleIndex = this.planCycleIndex(fromCycleIndex);
    const toSchedulePlan = {
      cycleIndex: toCycleIndex,
      scheduleInfo: this.getInfo(toCycleIndex)
    };
    const toScheduleInfo = toSchedulePlan.scheduleInfo;
    this.schedulePlan = toSchedulePlan;
    // Vyvolání události a vrácení výsledku.
    this.emitter.emit('gone', toScheduleInfo);
    return toScheduleInfo;
  }

  /** @inheritdoc */
  off(...args) {
    this.emitter.off(...args);
  }

  /** @inheritdoc */
  on(...args) {
    this.emitter.on(...args);
  }

  /** @inheritdoc */
  plan(offset) {
    if (!Numeric.isIndex(offset)) {
      throw new TypeError(`Invalid offset ${offset}.`);
    }
    return Array(offset)
      .fill()
      .reduce(
        (acc) => {
          const previousSchedulePlan = acc[acc.length - 1];
          const previousCycleIndex = get(
            previousSchedulePlan,
            'cycleIndex',
            null
          );
          const cycleIndex = this.planCycleIndex(previousCycleIndex);
          const scheduleInfo = this.getInfo(cycleIndex);
          return acc.concat({ cycleIndex, scheduleInfo });
        },
        [this.schedulePlan]
      )
      .slice(1)
      .map((schedulePlan) => schedulePlan.scheduleInfo);
  }

  /**
   * Naplánuje pořadí cyklu následujícího slajdu.
   * @param {null|number} fromCycleIndex Pořadí cyklu předchozího slajdu.
   * @private
   * @returns {number} Pořadí cyklu.
   */
  planCycleIndex(fromCycleIndex) {
    if (isNil(fromCycleIndex)) {
      // Bez výchozího cyklu se určují cykly od začátku. Číslování od nuly.
      return 0;
    }
    // Přičtení offsetu k aktuálnímu cyklu.
    return fromCycleIndex + 1;
  }

  /** @inheritdoc */
  skip() {
    // Není potřeba nic přeskakovat.
    // Přítomno pouze pro splnění rozhraní.
  }

  /** @inheritdoc */
  stop() {
    // Není potřeba nic zastavovat.
    // Přítomno pouze pro splnění rozhraní.
  }

  /** @inheritdoc */
  toString() {
    return `ScheduleDeterminitic {playlist: ${this.playlist}}`;
  }
}

export default ScheduleDeterministic;
